import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Save, Upload, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/ContentContext";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const inputC = "w-full bg-[#0f0f11] border border-white/15 focus:border-[#d4c4b7] text-stone-100 placeholder:text-stone-600 px-4 py-2.5 text-sm outline-none transition-colors";

function Text({ label, value, onChange, area }) {
  return (
    <div>
      {label && <label className="overline text-stone-500 block mb-2">{label}</label>}
      {area ? (
        <textarea rows={area} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputC} resize-none`} />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputC} />
      )}
    </div>
  );
}

function ListText({ label, value, onChange, rows = 4 }) {
  return <Text label={`${label} (one per line)`} area={rows} value={(value || []).join("\n")} onChange={(v) => onChange(v.split("\n").map((x) => x.trim()).filter(Boolean))} />;
}

function ImageField({ label, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/content/image", fd);
      onChange(`${BACKEND}${data.url}`);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <label className="overline text-stone-500 block mb-2">{label}</label>
      <div className="flex items-center gap-4">
        {value ? <img src={value} alt="" className="h-16 w-24 object-cover border border-white/15" /> : <div className="h-16 w-24 border border-dashed border-white/20 flex items-center justify-center text-stone-600 text-xs">none</div>}
        <label className="inline-flex items-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-300 hover:text-[#d4c4b7] px-4 py-2 text-xs uppercase tracking-[0.18em] cursor-pointer transition-colors">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
          <input type="file" accept="image/*" onChange={upload} className="hidden" />
        </label>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="border border-white/10 bg-[#0f0f11] p-6 md:p-8">
      <h2 className="font-serif text-2xl text-stone-50 mb-6">{title}</h2>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

export default function AdminContent() {
  const { user, ready: authReady } = useAuth();
  const navigate = useNavigate();
  const content = useSiteContent();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (authReady && !user) navigate("/admin/login"); }, [authReady, user, navigate]);

  useEffect(() => {
    if (content.ready && !s) {
      const cats = {};
      content.categories.forEach((c) => (cats[c.id] = { title: c.title, tag: c.tag, body: c.body, image: c.image }));
      const svc = {};
      Object.entries(content.services).forEach(([k, v]) => (svc[k] = { heroKicker: v.heroKicker, heroTitle: v.heroTitle, overview: v.overview, included: v.included, faqs: v.faqs, gallery: v.gallery }));
      const sub = {};
      content.suburbs.forEach((x) => (sub[x.slug] = { blurb: x.blurb }));
      setS({
        hero: { ...content.hero },
        specialty: { ...content.specialty },
        categories: cats,
        testimonials: content.testimonials.map((t) => ({ ...t })),
        partner: { ...content.partner },
        services: svc,
        suburbs: sub,
      });
    }
  }, [content.ready]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", s);
      await content.refresh();
      toast.success("Content saved — live now.");
    } catch {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  if (!authReady || !user || !s) {
    return <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#d4c4b7]" /></div>;
  }

  const setHero = (k, v) => setS((p) => ({ ...p, hero: { ...p.hero, [k]: v } }));
  const setSpec = (k, v) => setS((p) => ({ ...p, specialty: { ...p.specialty, [k]: v } }));
  const setPart = (k, v) => setS((p) => ({ ...p, partner: { ...p.partner, [k]: v } }));
  const setCat = (id, k, v) => setS((p) => ({ ...p, categories: { ...p.categories, [id]: { ...p.categories[id], [k]: v } } }));
  const setSvc = (id, k, v) => setS((p) => ({ ...p, services: { ...p.services, [id]: { ...p.services[id], [k]: v } } }));
  const setSub = (id, v) => setS((p) => ({ ...p, suburbs: { ...p.suburbs, [id]: { blurb: v } } }));
  const setTest = (i, k, v) => setS((p) => { const t = [...p.testimonials]; t[i] = { ...t[i], [k]: v }; return { ...p, testimonials: t }; });
  const addTest = () => setS((p) => ({ ...p, testimonials: [...p.testimonials, { quote: "", name: "", role: "" }] }));
  const delTest = (i) => setS((p) => ({ ...p, testimonials: p.testimonials.filter((_, x) => x !== i) }));
  const setFaq = (id, i, k, v) => setS((p) => { const f = [...(p.services[id].faqs || [])]; f[i] = { ...f[i], [k]: v }; return { ...p, services: { ...p.services, [id]: { ...p.services[id], faqs: f } } }; });
  const addFaq = (id) => setS((p) => ({ ...p, services: { ...p.services, [id]: { ...p.services[id], faqs: [...(p.services[id].faqs || []), { q: "", a: "" }] } } }));
  const delFaq = (id, i) => setS((p) => ({ ...p, services: { ...p.services, [id]: { ...p.services[id], faqs: p.services[id].faqs.filter((_, x) => x !== i) } } }));

  return (
    <div className="min-h-screen bg-[#0a0a0c]" data-testid="admin-content">
      <div className="grain-overlay" aria-hidden />
      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/brand/logo.png" alt="" className="h-10 w-auto object-contain" />
            <span className="hidden sm:block overline text-stone-500">Content</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" className="inline-flex items-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-300 hover:text-[#d4c4b7] px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors"><ArrowLeft className="h-3.5 w-3.5" /> Enquiries</Link>
            <button data-testid="content-save-top" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#d4c4b7] text-[#0a0a0c] hover:bg-white disabled:opacity-60 px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 md:px-10 py-10 space-y-8">
        <div>
          <h1 className="font-serif font-light text-stone-50 text-4xl md:text-5xl mb-2">Edit Website Content</h1>
          <p className="text-stone-400 font-light">Changes go live the moment you save.</p>
        </div>

        <Section title="Hero (top of homepage)">
          <Text label="Overline" value={s.hero.overline} onChange={(v) => setHero("overline", v)} />
          <ListText label="Heading lines" value={s.hero.lines} onChange={(v) => setHero("lines", v)} rows={3} />
          <Text label="Intro paragraph" area={3} value={s.hero.intro} onChange={(v) => setHero("intro", v)} />
          <ImageField label="Hero background image" value={s.hero.image} onChange={(v) => setHero("image", v)} />
        </Section>

        <Section title="About — Every Aspect of Tiling">
          <Text label="Overline" value={s.specialty.overline} onChange={(v) => setSpec("overline", v)} />
          <Text label="Title" value={s.specialty.title} onChange={(v) => setSpec("title", v)} />
          <Text label="Body" area={4} value={s.specialty.body} onChange={(v) => setSpec("body", v)} />
          <ListText label="Points" value={s.specialty.points} onChange={(v) => setSpec("points", v)} />
        </Section>

        <Section title="Service Categories (cards + service page headers/images)">
          {Object.keys(s.categories).map((id) => (
            <div key={id} className="border-t border-white/10 pt-5 space-y-4">
              <Text label="Title" value={s.categories[id].title} onChange={(v) => setCat(id, "title", v)} />
              <Text label="Tag" value={s.categories[id].tag} onChange={(v) => setCat(id, "tag", v)} />
              <Text label="Description" area={3} value={s.categories[id].body} onChange={(v) => setCat(id, "body", v)} />
              <ImageField label="Image" value={s.categories[id].image} onChange={(v) => setCat(id, "image", v)} />
            </div>
          ))}
        </Section>

        <Section title="Service Pages (detailed content)">
          {Object.keys(s.services).map((id) => (
            <details key={id} className="border-t border-white/10 pt-4">
              <summary className="cursor-pointer text-[#d4c4b7] font-serif text-xl py-2">{s.categories[id]?.title || id}</summary>
              <div className="space-y-4 pt-4">
                <Text label="Hero kicker" value={s.services[id].heroKicker} onChange={(v) => setSvc(id, "heroKicker", v)} />
                <Text label="Hero title" value={s.services[id].heroTitle} onChange={(v) => setSvc(id, "heroTitle", v)} />
                <ListText label="Overview paragraphs" value={s.services[id].overview} onChange={(v) => setSvc(id, "overview", v)} rows={4} />
                <ListText label="What's included" value={s.services[id].included} onChange={(v) => setSvc(id, "included", v)} rows={5} />
                <div>
                  <label className="overline text-stone-500 block mb-2">FAQs</label>
                  {(s.services[id].faqs || []).map((f, i) => (
                    <div key={i} className="border border-white/10 p-3 mb-3 space-y-2">
                      <div className="flex justify-between items-center"><span className="text-xs text-stone-500">Q{i + 1}</span><button onClick={() => delFaq(id, i)} className="text-stone-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button></div>
                      <Text value={f.q} onChange={(v) => setFaq(id, i, "q", v)} />
                      <Text area={2} value={f.a} onChange={(v) => setFaq(id, i, "a", v)} />
                    </div>
                  ))}
                  <button onClick={() => addFaq(id)} className="inline-flex items-center gap-1 text-xs text-[#d4c4b7]"><Plus className="h-3.5 w-3.5" /> Add FAQ</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[0, 1, 2].map((gi) => (
                    <ImageField key={gi} label={`Gallery image ${gi + 1}`} value={s.services[id].gallery?.[gi]} onChange={(v) => { const g = [...(s.services[id].gallery || [])]; g[gi] = v; setSvc(id, "gallery", g); }} />
                  ))}
                </div>
              </div>
            </details>
          ))}
        </Section>

        <Section title="Testimonials">
          {s.testimonials.map((t, i) => (
            <div key={i} className="border-t border-white/10 pt-4 space-y-3">
              <div className="flex justify-between items-center"><span className="text-xs text-stone-500">#{i + 1}</span><button onClick={() => delTest(i)} className="text-stone-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button></div>
              <Text label="Quote" area={2} value={t.quote} onChange={(v) => setTest(i, "quote", v)} />
              <div className="grid grid-cols-2 gap-4">
                <Text label="Name" value={t.name} onChange={(v) => setTest(i, "name", v)} />
                <Text label="Role / location" value={t.role} onChange={(v) => setTest(i, "role", v)} />
              </div>
            </div>
          ))}
          <button onClick={addTest} className="inline-flex items-center gap-1 text-xs text-[#d4c4b7]"><Plus className="h-3.5 w-3.5" /> Add testimonial</button>
        </Section>

        <Section title="Collaboration Partner">
          <Text label="Overline" value={s.partner.overline} onChange={(v) => setPart("overline", v)} />
          <Text label="Body" area={4} value={s.partner.body} onChange={(v) => setPart("body", v)} />
          <ListText label="Points" value={s.partner.points} onChange={(v) => setPart("points", v)} />
        </Section>

        <Section title="Suburb Page Intros">
          {Object.keys(s.suburbs).map((id) => (
            <Text key={id} label={id} value={s.suburbs[id].blurb} onChange={(v) => setSub(id, v)} />
          ))}
        </Section>

        <button data-testid="content-save-bottom" onClick={save} disabled={saving} className="inline-flex items-center gap-3 bg-[#d4c4b7] text-[#0a0a0c] hover:bg-white disabled:opacity-60 px-10 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save All Changes
        </button>
      </main>
    </div>
  );
}
